<template>
  <div class="flex flex-col h-full">
    <Teleport to="#topbar-search" defer>
      <SearchInput
        v-model="searchQuery"
        placeholder="Search by name or email"
        @update:model-value="handleSearch"
        class="w-full"
      />
    </Teleport>
    <!-- Header section -->
    <DashboardHeader title="Qualifying Applicants" link="/candidates">
      <template #right>
        <!-- Filter by batch -->
        <Select
          v-model="selectedBatch"
          placeholder="Filter by batch"
          @update:model-value="handleBatchFilter"
        >
          <SelectTrigger class="w-[180px] bg-white text-xs">
            <SelectValue placeholder="Select batch" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="batch in batchChoices" :key="batch.id" :value="batch.id">
              {{ batch.name }}
            </SelectItem>
          </SelectContent>
        </Select>

        <!-- Filter by program status -->
        <Select
          v-if="isGuide"
          v-model="selectedProgramStatus"
          placeholder="Filter by program status"
          @update:model-value="handleProgramStatusFilter"
        >
          <SelectTrigger class="w-[220px] bg-white text-xs">
            <SelectValue placeholder="Select program status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem v-for="status in referralQualifyingStatusOptions" :key="status.value" :value="status.value">
              {{ status.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <!-- Ordering -->
        <Select
          v-model="selectedOrdering"
          placeholder="Sort by"
          @update:model-value="handleOrderingChange"
        >
          <SelectTrigger class="w-[180px] bg-white text-xs">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="option in sortOptions" :key="option.value" :value="option.value">
              Sort by {{ option.label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </template>
    </DashboardHeader>
    <p class="text-sm text-gray-500 my-2">List of applicants who have graduated and are currently applying for roles</p>
    
    <!-- Main content section -->
    <div
      class="fadeIn flex w-full flex-1 overflow-y-auto border-[1px] border-gray-200 rounded-xl bg-white"
    >
      <div class="w-full">
        <ApplicantsTable
          :applicants="applicantsStore.applicants"
          :loading="applicantsStore.loading"
          :has-applicants="applicantsStore.hasApplicants"
          :get-batch-name="getBatchName"
          :ordering="applicantsStore.filters.ordering"
        />

        <PaginationComponent
          v-if="applicantsStore.isPaginated && applicantsStore.hasApplicants"
          :current-page="applicantsStore.pagination.currentPage"
          :total-pages="applicantsStore.pagination.totalPages"
          @update:page="handlePageChange"
          class="py-4"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SearchInput from '@/components/common/SearchInput.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import ApplicantsTable from '@/components/applicants/ApplicantsTable.vue'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { useBatchChoices } from '@/composables/useBatchChoices'
import { useApplicantsStore } from '@/stores/applicants'
import { useDebounce } from '@/composables/useDebounce'
import DashboardHeader from '@/components/common/DashboardHeader.vue'
import { referralQualifyingStatusOptions } from '@/const/fellowStatuses'
import { useAuth } from '@/composables/useAuth'

const route = useRoute()
const router = useRouter()
const { batchChoices, fetchBatchChoices, getBatchName } = useBatchChoices()
const applicantsStore = useApplicantsStore()
const { isGuide } = useAuth()

// Sort options
const sortOptions = [
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: '-batch', label: 'Batch (Newest First)' },
  { value: 'batch', label: 'Batch (Oldest First)' }
]

// Local state for form inputs
const searchQuery = ref(route.query.search?.toString() || '')
const selectedBatch = ref(route.query.batch?.toString() || 'all')
const selectedProgramStatus = ref(route.query.outtalent_program_status?.toString() || 'all')
const selectedOrdering = ref(route.query.ordering?.toString() || 'first_name')

// Create debounced search handler
const debouncedSearch = useDebounce((value: string) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicantsStore.setSearch(router, value)
}, 500)

// Initialize from URL
onMounted(async () => {
  await fetchBatchChoices()
  // Add "All batches" option at the beginning if not present
  if (!batchChoices.value.find((b) => b.id === 'all')) {
    batchChoices.value = [{ id: 'all', name: 'All batches' }, ...batchChoices.value]
  }

  // Initialize store from URL and fetch data
  applicantsStore.initFromUrl(route.query as Record<string, string>)
  await applicantsStore.fetchApplicants({ page: applicantsStore.pagination.currentPage })
})

// Watch for route changes not initiated by our store
watch(
  () => route.query,
  (query) => {
    // Update local form state
    searchQuery.value = query.search?.toString() || ''
    selectedBatch.value = query.batch?.toString() || 'all'
    selectedProgramStatus.value = query.outtalent_program_status?.toString() || 'all'
    selectedOrdering.value = query.ordering?.toString() || 'first_name'

    // Update store and refetch if needed
    applicantsStore.initFromUrl(query as Record<string, string>)
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    applicantsStore.fetchApplicants({ page: applicantsStore.pagination.currentPage })
  }
)

// Handlers
const handleSearch = () => {
  debouncedSearch(searchQuery.value)
}

const handleBatchFilter = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicantsStore.setBatch(router, selectedBatch.value)
}

const handleProgramStatusFilter = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicantsStore.setProgramStatus(router, selectedProgramStatus.value)
}

const handlePageChange = (page: number) => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicantsStore.setPage(router, page)
}

const handleOrderingChange = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicantsStore.setOrdering(router, selectedOrdering.value)
}
</script>

<style scoped></style>
